import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getSpeakers,
  getSpeakerById,
  createSpeaker,
  updateSpeaker,
  deleteSpeaker,
  checkSpeakerStatus,
  testSpeakerPlayback,
  getSpeakerRouteConfig,
  putSpeakerRouteConfig,
} from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import PageHeader from './PageHeader';

/** Defaults for Add speaker modal (user can change any field). */
const SPEAKER_FIELD_DEFAULTS = {
  volume: 10,
  sample_rate: '8000',
  channels: '1',
  codec: 'pcm_s16le',
  timeout_seconds: '10',
  tmp_dir: '/tmp',
  play_mode: 'noloop',
};

const EMPTY_FORM = {
  label: '',
  description: '',
  host: '',
  username: '',
  password: '',
  ...SPEAKER_FIELD_DEFAULTS,
};

const DOT_FALLBACK = ['#14b8a6', '#4f46e5', '#94a3b8', '#f59e0b', '#ef4444'];

function coalesceSave(form) {
  const num = (v, def) => {
    if (v === '' || v === undefined || v === null) return def;
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };
  const str = (v, def) => (v === undefined || v === null || String(v).trim() === '' ? def : String(v).trim());

  return {
    label: str(form.label, ''),
    description: form.description?.trim() || null,
    host: str(form.host, ''),
    username: str(form.username, 'admin'),
    password: form.password,
    volume: num(form.volume, SPEAKER_FIELD_DEFAULTS.volume),
    sample_rate: num(form.sample_rate, 8000),
    channels: num(form.channels, 1),
    codec: str(form.codec, 'pcm_s16le'),
    timeout_seconds: num(form.timeout_seconds, 10),
    tmp_dir: str(form.tmp_dir, '/tmp'),
    play_mode: str(form.play_mode, 'noloop'),
  };
}

const SpeakerConfiguration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [speakers, setSpeakers] = useState([]);
  const [routeConfig, setRouteConfig] = useState({ in_speakers: [], out_speakers: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [statusMap, setStatusMap] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [setupTab, setSetupTab] = useState('IN');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDirection, setAssignDirection] = useState('IN');
  const [assignSelectedId, setAssignSelectedId] = useState('');
  const [routeSaving, setRouteSaving] = useState(false);
  const [modalAlert, setModalAlert] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadRouteConfig = useCallback(async () => {
    try {
      const data = await getSpeakerRouteConfig();
      const rc = data?.route_config || {};
      setRouteConfig({
        in_speakers: Array.isArray(rc.in_speakers) ? rc.in_speakers : [],
        out_speakers: Array.isArray(rc.out_speakers) ? rc.out_speakers : [],
      });
    } catch (_) {
      setRouteConfig({ in_speakers: [], out_speakers: [] });
    }
  }, []);

  const loadSpeakers = useCallback(async () => {
    setLoading(true);
    setError('');
    let list = [];
    try {
      const data = await getSpeakers();
      list = Array.isArray(data?.speakers) ? data.speakers : [];
      setSpeakers(list);
    } catch (e) {
      setError(e.message || 'Failed to load speakers');
      setSpeakers([]);
      list = [];
    } finally {
      setLoading(false);
    }
    return list;
  }, []);

  const renderUserTimestampCell = (nameValue, timestampValue) => {
    const name = nameValue || '-';
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(timestampValue)}
        </Typography>
      </Stack>
    );
  };

  const runSpeakerStatusCheck = useCallback(async (sp) => {
    if (!sp?.id) return;
    setStatusLoading((m) => ({ ...m, [sp.id]: true }));
    try {
      const res = await checkSpeakerStatus(sp.id);
      setStatusMap((m) => ({
        ...m,
        [sp.id]: { status: res.status, message: res.message || '' },
      }));
    } catch (e) {
      setStatusMap((m) => ({
        ...m,
        [sp.id]: { status: 'error', message: e.message || 'Check failed' },
      }));
    } finally {
      setStatusLoading((m) => ({ ...m, [sp.id]: false }));
    }
  }, []);

  const refreshStatus = (sp) => runSpeakerStatusCheck(sp);

  const refreshAllSpeakerStatuses = useCallback(async (list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    await Promise.allSettled(list.map((sp) => runSpeakerStatusCheck(sp)));
  }, [runSpeakerStatusCheck]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const list = await loadSpeakers();
      await loadRouteConfig();
      if (cancelled || !list.length) return;
      await refreshAllSpeakerStatuses(list);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadSpeakers, loadRouteConfig, refreshAllSpeakerStatuses]);


  const handleToolbarRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const data = await getSpeakers();
      const list = Array.isArray(data?.speakers) ? data.speakers : [];
      setSpeakers(list);
      await loadRouteConfig();
      await refreshAllSpeakerStatuses(list);
    } catch (e) {
      setError(e.message || 'Failed to load speakers');
      setSpeakers([]);
    } finally {
      setRefreshing(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowPassword(true);
    setModalAlert(null);
    setModalOpen(true);
  };

  const openEdit = async (sp) => {
    try {
      const full = await getSpeakerById(sp.id);
      setEditingId(sp.id);
      setForm({
        label: full.label || '',
        description: full.description || '',
        host: full.host || '',
        username: full.username || '',
        password: full.password || '',
        volume: full.volume ?? SPEAKER_FIELD_DEFAULTS.volume,
        sample_rate: full.sample_rate != null ? String(full.sample_rate) : SPEAKER_FIELD_DEFAULTS.sample_rate,
        channels: full.channels != null ? String(full.channels) : SPEAKER_FIELD_DEFAULTS.channels,
        codec: full.codec || SPEAKER_FIELD_DEFAULTS.codec,
        timeout_seconds:
          full.timeout_seconds != null ? String(full.timeout_seconds) : SPEAKER_FIELD_DEFAULTS.timeout_seconds,
        tmp_dir: full.tmp_dir || SPEAKER_FIELD_DEFAULTS.tmp_dir,
        play_mode: full.play_mode || SPEAKER_FIELD_DEFAULTS.play_mode,
      });
      setShowPassword(false);
      setModalAlert(null);
      setModalOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load speaker');
    }
  };

  const handleSave = async () => {
    const payload = coalesceSave(form);
    if (!payload.label || !payload.host) {
      const msg = 'Label and IP are required';
      setModalAlert({ message: msg, severity: 'warning' });
      showSnackbar(msg, 'warning');
      return;
    }
    if (!editingId && !form.password) {
      const msg = 'Password is required for a new speaker';
      setModalAlert({ message: msg, severity: 'warning' });
      showSnackbar(msg, 'warning');
      return;
    }
    setSaving(true);
    setModalAlert(null);
    try {
      if (editingId) {
        const p = { ...payload };
        delete p.password;
        if (form.password) p.password = form.password;
        await updateSpeaker(editingId, p);
      } else {
        await createSpeaker({ ...payload, password: payload.password });
      }
      setModalOpen(false);
      await loadSpeakers();
    } catch (e) {
      const msg = e.message || 'Save failed';
      setModalAlert({ message: msg, severity: 'error' });
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteSpeaker(deleteTarget.id);
      setDeleteTarget(null);
      await loadSpeakers();
      await loadRouteConfig();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (sp) => {
    setTestingId(sp.id);
    try {
      await testSpeakerPlayback(sp.id);
    } catch (e) {
      setError(e.message || 'Test playback failed');
    } finally {
      setTestingId(null);
    }
  };

  const openAssign = (direction) => {
    setAssignDirection(direction);
    setAssignSelectedId('');
    setAssignOpen(true);
  };

  const persistRoute = async (inSpeakers, outSpeakers, { closeAssign = false } = {}) => {
    setRouteSaving(true);
    try {
      await putSpeakerRouteConfig({
        in_speaker_ids: inSpeakers.map((s) => s.id),
        out_speaker_ids: outSpeakers.map((s) => s.id),
      });
      await loadRouteConfig();
      if (closeAssign) {
        setAssignOpen(false);
        setAssignSelectedId('');
      }
    } catch (e) {
      setError(e.message || 'Could not save route assignment');
    } finally {
      setRouteSaving(false);
    }
  };

  const addToRoute = async () => {
    if (!assignSelectedId) {
      showSnackbar('Select a speaker', 'warning');
      return;
    }
    const sp = speakers.find((s) => s.id === assignSelectedId);
    if (!sp) return;
    const inSp = [...(routeConfig.in_speakers || [])];
    const outSp = [...(routeConfig.out_speakers || [])];
    if (assignDirection === 'IN') {
      if (inSp.some((s) => s.id === sp.id)) {
        showSnackbar('This speaker is already assigned to IN', 'info');
        return;
      }
      inSp.push(sp);
    } else {
      if (outSp.some((s) => s.id === sp.id)) {
        showSnackbar('This speaker is already assigned to OUT', 'info');
        return;
      }
      outSp.push(sp);
    }
    await persistRoute(inSp, outSp, { closeAssign: true });
  };

  const removeFromRoute = async (direction, speakerId) => {
    const inSp = [...(routeConfig.in_speakers || [])];
    const outSp = [...(routeConfig.out_speakers || [])];
    if (direction === 'IN') {
      await persistRoute(
        inSp.filter((s) => s.id !== speakerId),
        outSp,
      );
    } else {
      await persistRoute(
        inSp,
        outSp.filter((s) => s.id !== speakerId),
      );
    }
  };

  const statusLabel = (sp) => {
    const loading = statusLoading[sp.id];
    if (loading) return 'Checking';
    const s = statusMap[sp.id]?.status;
    if (s === 'online') return 'Online';
    if (s === 'offline') return 'Offline';
    if (s === 'error') return 'Error';
    return 'Unknown';
  };

  const statusStyles = (sp) => {
    const label = statusLabel(sp);
    if (label === 'Online') return { bgcolor: '#dcfce7', color: '#166534' };
    if (label === 'Offline') return { bgcolor: '#fee2e2', color: '#991b1b' };
    if (label === 'Error') return { bgcolor: '#fde68a', color: '#92400e' };
    if (label === 'Checking') return { bgcolor: '#e5e7eb', color: '#4b5563' };
    return { bgcolor: '#e5e7eb', color: '#4b5563' };
  };

  const renderSetupLane = (direction) => {
    const assignedList = direction === 'IN' ? routeConfig.in_speakers || [] : routeConfig.out_speakers || [];
    const canOpenAddDialog = speakers.length > 0 && !loading && !routeSaving;

    return (
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {direction} — assigned speakers
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => openAssign(direction)}
            disabled={!canOpenAddDialog}
            startIcon={<CheckCircleIcon fontSize="small" />}
            fullWidth={isMobile}
            sx={{
              minWidth: 96,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#40518A',
              boxShadow: '0 4px 12px rgba(64, 81, 138, 0.28)',
              '&:hover': {
                bgcolor: '#32406d',
                boxShadow: '0 6px 14px rgba(64, 81, 138, 0.34)',
              },
            }}
          >
            Add speaker
          </Button>
        </Stack>

        {assignedList.length === 0 ? (
          <Alert severity="info" sx={isMobile ? { mx: 2, mb: 2 } : undefined}>
            No {direction} speakers assigned yet. Use &quot;Add speaker&quot; to assign one or more devices.
          </Alert>
        ) : isMobile ? (
          <Stack spacing={1.5} sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 2, sm: 0 } }}>
            {assignedList.map((sp, index) => {
              const dotColor = sp.label_color || DOT_FALLBACK[index % DOT_FALLBACK.length];
              return (
                <Card key={sp.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <FiberManualRecordIcon sx={{ fontSize: 12, color: dotColor }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                        {sp.label || 'Speaker'}
                      </Typography>
                    </Stack>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        IP
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {sp.host}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Created by
                        </Typography>
                        {renderUserTimestampCell(sp.created_by_name || sp.created_by, sp.created_at)}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Updated by
                        </Typography>
                        {renderUserTimestampCell(sp.updated_by_name || sp.updated_by, sp.updated_at)}
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      fullWidth
                      disabled={routeSaving}
                      onClick={() => removeFromRoute(direction, sp.id)}
                      sx={{ fontWeight: 600 }}
                    >
                      Remove
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Speaker label</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created by</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Updated by</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right', width: 120 }}>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedList.map((sp, index) => {
                  const dotColor = sp.label_color || DOT_FALLBACK[index % DOT_FALLBACK.length];
                  return (
                    <TableRow key={sp.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <FiberManualRecordIcon sx={{ fontSize: 14, color: dotColor }} />
                          <Typography sx={{ fontWeight: 600 }}>{sp.label || 'Speaker'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {sp.host}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {renderUserTimestampCell(sp.created_by_name || sp.created_by, sp.created_at)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {renderUserTimestampCell(sp.updated_by_name || sp.updated_by, sp.updated_at)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          disabled={routeSaving}
                          onClick={() => removeFromRoute(direction, sp.id)}
                          sx={{ fontWeight: 600 }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Speaker configuration" subtitle={null} icon={null} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Configured Speakers
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={openAdd}
              startIcon={<VolumeUpIcon />}
              fullWidth={isMobile}
            >
              Add speaker
            </Button>
            <Button
              variant="outlined"
              onClick={handleToolbarRefresh}
              disabled={loading || refreshing}
              startIcon={loading || refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              fullWidth={isMobile}
            >
              {loading ? 'Loading...' : refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <LinearProgress />
        ) : speakers.length > 0 ? (
          isMobile ? (
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {speakers.map((sp, index) => {
                const dotColor = sp.label_color || DOT_FALLBACK[index % DOT_FALLBACK.length];
                return (
                  <Card key={sp.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 12, color: dotColor }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                          {sp.label || 'Speaker'}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                          <Tooltip title={statusMap[sp.id]?.message || ''}>
                            <Chip
                              size="small"
                              label={statusLabel(sp)}
                              sx={{ minWidth: 72, ...statusStyles(sp), fontWeight: 600 }}
                            />
                          </Tooltip>
                          <Tooltip title="Refresh status">
                            <span>
                              <IconButton size="small" onClick={() => refreshStatus(sp)} disabled={statusLoading[sp.id]}>
                                {statusLoading[sp.id] ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                      <Stack spacing={0.75}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Description:</strong> {sp.description || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>IP:</strong>{' '}
                          <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontWeight: 600 }}>
                            {sp.host}
                          </Box>
                        </Typography>
                      </Stack>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mt: 1.25 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Created by
                          </Typography>
                          {renderUserTimestampCell(sp.created_by_name || sp.created_by, sp.created_at)}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Updated by
                          </Typography>
                          {renderUserTimestampCell(sp.updated_by_name || sp.updated_by, sp.updated_at)}
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VolumeUpIcon />}
                          fullWidth
                          onClick={() => handleTest(sp)}
                          disabled={testingId === sp.id}
                        >
                          Test sound
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} fullWidth onClick={() => openEdit(sp)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          fullWidth
                          onClick={() => setDeleteTarget(sp)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1100 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Speaker label</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created by</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Updated by</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {speakers.map((sp, index) => {
                    const dotColor = sp.label_color || DOT_FALLBACK[index % DOT_FALLBACK.length];
                    return (
                      <TableRow key={sp.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <FiberManualRecordIcon sx={{ fontSize: 14, color: dotColor }} />
                            <Typography sx={{ fontWeight: 600 }}>{sp.label || 'Speaker'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', maxWidth: 220 }}>
                          {sp.description || '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {sp.host}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title={statusMap[sp.id]?.message || ''}>
                              <Chip
                                size="small"
                                label={statusLabel(sp)}
                                sx={{ minWidth: 86, ...statusStyles(sp), fontWeight: 600 }}
                              />
                            </Tooltip>
                            <Tooltip title="Refresh status">
                              <span>
                                <IconButton size="small" onClick={() => refreshStatus(sp)} disabled={statusLoading[sp.id]}>
                                  {statusLoading[sp.id] ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          {renderUserTimestampCell(sp.created_by_name || sp.created_by, sp.created_at)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          {renderUserTimestampCell(sp.updated_by_name || sp.updated_by, sp.updated_at)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Test sound">
                              <span>
                                <IconButton size="small" onClick={() => handleTest(sp)} disabled={testingId === sp.id}>
                                  {testingId === sp.id ? <CircularProgress size={16} /> : <VolumeUpIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(sp)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(sp)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
            No speakers configured yet. Click &quot;Add speaker&quot; to register an IP speaker device.
          </Typography>
        )}
      </Card>

      <Typography variant="h6" sx={{ mt: { xs: 4, sm: 6, md: 8 }, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        Speaker Setup & Configuration
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={setupTab}
          onChange={(_, v) => setSetupTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab label="IN" value="IN" />
          <Tab label="OUT" value="OUT" />
        </Tabs>
      </Box>

      {setupTab === 'IN' && renderSetupLane('IN')}
      {setupTab === 'OUT' && renderSetupLane('OUT')}

      <Dialog
        open={modalOpen}
        onClose={() => {
          if (saving) return;
          setModalOpen(false);
          setModalAlert(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: { xs: '1.05rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          {editingId ? 'Edit speaker' : 'Add speaker'}
          <IconButton
            onClick={() => {
              if (saving) return;
              setModalOpen(false);
              setModalAlert(null);
            }}
            size="small"
            disabled={saving}
            sx={{ bgcolor: { xs: '#f1f5f9', sm: 'transparent' }, '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {modalAlert && (
              <Alert
                severity={modalAlert.severity}
                onClose={() => setModalAlert(null)}
                sx={{ alignItems: 'center' }}
              >
                {modalAlert.message}
              </Alert>
            )}
            <TextField
              label="Speaker label"
              fullWidth
              required
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="e.g. Main entrance speaker"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional notes"
            />
            <TextField
              label="IP"
              fullWidth
              required
              value={form.host}
              onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              placeholder="192.168.1.81"
            />
            <TextField
              label="Username"
              fullWidth
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              placeholder="admin"
            />
            <TextField
              label="Password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={editingId ? 'Leave blank to keep current' : 'Speaker web password'}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Volume (0–15)
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  value={typeof form.volume === 'number' ? form.volume : Number(form.volume) || 0}
                  onChange={(_, v) => setForm((f) => ({ ...f, volume: v }))}
                  min={0}
                  max={15}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" sx={{ minWidth: 24, fontWeight: 700 }}>
                  {typeof form.volume === 'number' ? form.volume : Number(form.volume) || 0}
                </Typography>
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Sample rate (Hz)"
                type="number"
                fullWidth
                value={form.sample_rate}
                onChange={(e) => setForm((f) => ({ ...f, sample_rate: e.target.value }))}
                placeholder="8000"
              />
              <TextField
                label="Channels"
                type="number"
                fullWidth
                value={form.channels}
                onChange={(e) => setForm((f) => ({ ...f, channels: e.target.value }))}
                placeholder="1"
              />
            </Stack>
            <TextField
              label="Codec"
              fullWidth
              value={form.codec}
              onChange={(e) => setForm((f) => ({ ...f, codec: e.target.value }))}
              placeholder="pcm_s16le"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Timeout (seconds)"
                type="number"
                fullWidth
                value={form.timeout_seconds}
                onChange={(e) => setForm((f) => ({ ...f, timeout_seconds: e.target.value }))}
                placeholder="10"
              />
              <TextField
                label="Play mode"
                fullWidth
                value={form.play_mode}
                onChange={(e) => setForm((f) => ({ ...f, play_mode: e.target.value }))}
                placeholder="noloop"
              />
            </Stack>
            <TextField
              label="Temp directory"
              fullWidth
              value={form.tmp_dir}
              onChange={(e) => setForm((f) => ({ ...f, tmp_dir: e.target.value }))}
              placeholder="/tmp"
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            '& > button': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          <Button
            onClick={() => {
              setModalOpen(false);
              setModalAlert(null);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={assignOpen}
        onClose={() => !routeSaving && setAssignOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' }, py: { xs: 1.5, sm: 2 } }}>
          Add speaker to {assignDirection}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <FormControl fullWidth sx={{ mt: { xs: 0, sm: 1 } }}>
            <InputLabel id="assign-speaker-label">Speaker</InputLabel>
            <Select
              labelId="assign-speaker-label"
              label="Speaker"
              value={assignSelectedId}
              onChange={(e) => setAssignSelectedId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select…</em>
              </MenuItem>
              {speakers
                .filter((s) => {
                  const list = assignDirection === 'IN' ? routeConfig.in_speakers || [] : routeConfig.out_speakers || [];
                  return !list.some((a) => a.id === s.id);
                })
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label} — {s.host}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {speakers.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Add a speaker in Configured Speakers first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            '& > button': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          <Button onClick={() => setAssignOpen(false)} disabled={routeSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={addToRoute} disabled={routeSaving}>
            {routeSaving ? <CircularProgress size={22} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !saving && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>Delete speaker?</DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            Remove <strong>{deleteTarget?.label}</strong> ({deleteTarget?.host})? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            '& > button': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpeakerConfiguration;
